import { useState } from 'react'

import { isNumber } from 'lodash'
import { useTranslation } from 'react-i18next'

import {
    IconChatOutline,
    IconHeartFill,
    IconHeartOutline,
    IconLiveCircleTip,
    IconPostStats
} from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import TipModal from 'components/UI/Modal/Tip/TipModal'
import { ViewLikesModal } from 'components-ref/organisms'
import { useModalContext } from 'context/modalContext'
import { toFixedNumber } from 'helpers/util'
import './_postSidebar.scss'
import { useUserProfile } from 'store'

interface Props {
    hideTip: boolean
    like: {
        status: boolean
        count: number
    }
    modelData: {
        postId: string
        modelId: string
        avatarSrc: string
    }
    commentsCount: number
    isEnabled: boolean
    amount: number
    isOwner: boolean
    likePost: () => void
    openStats: () => void
    openComments: () => void
    onTipModal?: () => void
    isPlaying?: boolean
    isSponsoredPost?: boolean
    hideComments?: boolean
}

export const PostSidebar = ({
    hideTip,
    like,
    modelData,
    commentsCount,
    isEnabled,
    amount,
    likePost,
    openStats,
    openComments,
    onTipModal,
    isOwner,
    isPlaying,
    isSponsoredPost,
    hideComments = false,
}: Props) => {
    const modalData = useModalContext()
    const { t } = useTranslation()
    const [showLikesModal, setShowLikesModal] = useState(false)
    const userData = useUserProfile()

    const addTip = () => {
        if (
            userData?.status === 'restricted' &&
            userData?.role === 'fan'
        ) {
            addToast('error', 'Your account is restricted.')
            return
        }

        modalData.addModal(
            t('tipAmount'),
            <TipModal
                tipType='post'
                modelData={modelData}
            />,
            false,
            false,
            '',
            '',
            true // keyboardAdjust
        )

        onTipModal?.()
    }

    const showLikes = () => {
        setShowLikesModal(true)
    }

    const handleClickLike = (
        e: React.MouseEvent<HTMLDivElement>
    ) => {
        e.preventDefault()
        e.stopPropagation()

        if (!isEnabled) {
            return
        }

        if (!isOwner) {
            likePost()
        } else {
            showLikes()
        }
    }

    return (
        <>
            {showLikesModal && (
                <ViewLikesModal
                    onClose={() =>
                        setShowLikesModal(false)
                    }
                    postId={modelData.postId}
                />
            )}

            {/*
                TEMP:
                hideComments currently hides the entire sidebar for
                purchased message items.

                In the future, revert this to:
                {!isPlaying && ( ... )}

                and restore the individual {!hideComments && (...)}
                wrapper around the comments button only.
            */}
            {!isPlaying && !hideComments && (
                <div className='postSidebar'>
                    {isOwner && (
                        <div
                            className='postSidebar__action'
                            onClick={openStats}
                        >
                            <div>
                                <IconPostStats />
                            </div>

                            <p>
                                $
                                {Number.isInteger(amount)
                                    ? amount
                                    : toFixedNumber(
                                        amount,
                                        2
                                    )}
                            </p>
                        </div>
                    )}

                    <div
                        className='postSidebar__action'
                        onClick={handleClickLike}
                    >
                        <div>
                            {like.status ? (
                                <IconHeartFill />
                            ) : (
                                <IconHeartOutline
                                    color={
                                        isEnabled
                                            ? '#fff'
                                            : '#afafaf'
                                    }
                                />
                            )}
                        </div>

                        {isNumber(like.count) &&
                            !Number.isNaN(
                                like.count
                            ) &&
                            like.count > 0 && (
                                <p>
                                    {like.count}
                                </p>
                            )}
                    </div>

                    <div
                        className='postSidebar__action'
                        onClick={() => {
                            if (!isEnabled) {
                                return
                            }

                            openComments()
                        }}
                    >
                        <div>
                            <IconChatOutline
                                color={
                                    isEnabled
                                        ? '#fff'
                                        : '#afafaf'
                                }
                            />
                        </div>

                        <p>{commentsCount}</p>
                    </div>

                    {!hideTip && !isSponsoredPost && (
                        <div
                            className='postSidebar__action'
                            onClick={() => {
                                if (!isEnabled) {
                                    return
                                }

                                addTip()
                            }}
                        >
                            <div>
                                <IconLiveCircleTip
                                    color={
                                        isEnabled
                                            ? '#fff'
                                            : '#afafaf'
                                    }
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    )
}

/* import { useState } from 'react'

import { isNumber } from 'lodash'
import { useTranslation } from 'react-i18next'

import {
    IconChatOutline,
    IconHeartFill,
    IconHeartOutline,
    IconLiveCircleTip,
    IconPostStats
} from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import TipModal from 'components/UI/Modal/Tip/TipModal'
import { ViewLikesModal } from 'components-ref/organisms'
import { useModalContext } from 'context/modalContext'
import { toFixedNumber } from 'helpers/util'
import './_postSidebar.scss'
import { useUserProfile } from 'store'

interface Props {
    hideTip: boolean
    like: {
        status: boolean
        count: number
    }
    modelData: {
        postId: string
        modelId: string
        avatarSrc: string
    }
    commentsCount: number
    isEnabled: boolean
    amount: number
    isOwner: boolean
    likePost: () => void
    openStats: () => void
    openComments: () => void
    onTipModal?: () => void
    isPlaying?: boolean
    isSponsoredPost?: boolean
    hideComments?: boolean // HIDE COMMENTS IS FOR PURCHASERS FEED FOR MESSAGES - CAN USE IT TO HIDE OTHER BUTTONS
}
export const PostSidebar = ({
    hideTip,
    like,
    modelData,
    commentsCount,
    isEnabled,
    amount,
    likePost,
    openStats,
    openComments,
    onTipModal,
    isOwner,
    isPlaying,
    isSponsoredPost,
    hideComments = false,
}: Props) => {
    const modalData = useModalContext()
    const { t } = useTranslation()
    const [showLikesModal, setShowLikesModal] = useState(false)
    const userData = useUserProfile();
    const addTip = () => {
        if(userData?.status === 'restricted' && userData?.role === 'fan'){
            addToast('error', 'Your account is restricted.');
            return;
        }
        modalData.addModal(
            t('tipAmount'),
            <TipModal tipType='post' modelData={modelData} />
        )
        onTipModal?.()
    }

    const showLikes = () => {
        setShowLikesModal(true)
    }

const handleClickLike = (
    e: React.MouseEvent<HTMLDivElement>
) => {
    e.preventDefault()
    e.stopPropagation()

    if (!isEnabled) {
        return
    }

    if (!isOwner) {
        likePost()
    } else {
        showLikes()
    }
}

    return (
        <>
            {showLikesModal && (
                <ViewLikesModal
                    onClose={() => setShowLikesModal(false)}
                    postId={modelData.postId}
                />
            )}
            {!isPlaying && (
                <div className='postSidebar'>
                    {isOwner && (
                        <div
                            className='postSidebar__action'
                            onClick={() => {
                                openStats()
                            }}
                        >
                            <div>
                                <IconPostStats />
                            </div>
                            <p>
                                $
                                {Number.isInteger(amount)
                                    ? amount
                                    : toFixedNumber(amount, 2)}
                            </p>
                        </div>
                    )}

                    <div className='postSidebar__action' onClick={handleClickLike}>
                        <div>
                            {like.status ? (
                                <IconHeartFill />
                            ) : (
                                <IconHeartOutline
                                    color={isEnabled ? '#fff' : '#afafaf'}
                                />
                            )}
                        </div>
                        {isNumber(like.count) && !Number.isNaN(like.count) && like.count > 0 && (
                        <p>{like.count}</p>
                        )}
                    </div>

                    {!hideComments && (
                        <div
                        className='postSidebar__action'
                        onClick={() => {
                            if (!isEnabled) return
                            openComments()
                        }}
                        >
                        <div>
                            <IconChatOutline color={isEnabled ? '#fff' : '#afafaf'} />
                        </div>
                        <p>{commentsCount}</p>
                        </div>
                    )}
                    {!hideTip && !isSponsoredPost && (
                        <div
                            className='postSidebar__action'
                            onClick={() => {
                                if (!isEnabled) return
                                addTip()
                            }}
                        >
                            <div>
                                <IconLiveCircleTip
                                    color={isEnabled ? '#fff' : '#afafaf'}
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    )

}
 */